import { useEffect, useState, useRef, memo } from "react";
import axios from "axios";
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";
import { CiEdit } from "react-icons/ci";
import { MdFileDownload } from "react-icons/md";
import { IoSearch } from "react-icons/io5";
import { toast } from "react-hot-toast";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import Modal from "react-modal";
import trashIcon from "../assets/icon/trash.svg";
import courseListIcon from "../assets/icon/receipt-text.svg";
import backarrow from "../assets/icon/arrow-right.svg";
import {
  FaBook,
  FaBookOpen,
  FaRegListAlt,
  FaDatabase,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaCalculator,
  FaChartLine,
  FaCoins,
  FaPenFancy,
  FaRegFileAlt,
  FaPencilAlt,
} from "react-icons/fa";

const AnimatedBackground = memo(() => {
  const icons = [
    FaBook,
    FaBookOpen,
    FaRegListAlt,
    FaDatabase,
    FaUserGraduate,
    FaChalkboardTeacher,
    FaCalculator,
    FaChartLine,
    FaCoins,
    FaPenFancy,
    FaRegFileAlt,
    FaPencilAlt,
  ];

  const rows = 5;
  const cols = 11;
  const iconArray = [];

  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      const Icon = icons[Math.floor(Math.random() * icons.length)];
      iconArray.push({
        Icon,
        size: 18 + Math.random() * 20,
        top: (i / rows) * 100 + Math.random() * 5,
        left: (j / cols) * 100 + Math.random() * 5,
        duration: 15 + Math.random() * 10,
        opacity: 0.15 + Math.random() * 0.25,
        xOffset: Math.random() * 10 - 5,
        yOffset: Math.random() * 10 - 5,
      });
    }
  }

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {iconArray.map((item, i) => (
        <motion.div
          key={i}
          className="absolute text-white/50"
          style={{
            top: `${item.top}%`,
            left: `${item.left}%`,
            opacity: item.opacity,
          }}
          initial={{ scale: 0.5 }}
          animate={{
            x: [0, item.xOffset, 0],
            y: [0, item.yOffset, 0],
            scale: [0.5, 1, 0.5],
          }}
          transition={{
            duration: item.duration,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <item.Icon size={item.size} />
        </motion.div>
      ))}
    </div>
  );
});

const AllStudents = () => {
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_URL;

  const [students, setStudents] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");

  const searchRef = useRef("");
  const debounceRef = useRef(null);

  const fetchStudents = async (pageNum = 1) => {
    setLoading(true);

    const limit = window.innerHeight < 1000 ? 5 : 10;

    try {
      const res = await axios.get(`${API_BASE}/api/student/Get-All-Student`, {
        params: {
          search: searchRef.current.trim(),
          page: pageNum,
          limit,
        },
        headers: { Authorization: localStorage.getItem("token") },
      });

      setStudents(res.data.data.students || []);
      setTotalPages(res.data.data.totalPages || 1);
      setPage(pageNum);
    } catch {
      toast.error("خطا در دریافت دانشجویان");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents(page);
  }, [page]);

  const handleSearch = (value) => {
    searchRef.current = value;
    setPage(1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchStudents(1), 500);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const deleteUrl = `${API_BASE}/api/student/Delete-Student/${deleteId}`;

      await axios.delete(deleteUrl, {
        headers: { Authorization: localStorage.getItem("token") },
      });

      toast.success("دانشجو با موفقیت حذف شد");

      setStudents((prev) => prev.filter((t) => t._id !== deleteId));

      if (students.length === 1 && page > 1) {
        setPage((p) => p - 1);
        fetchStudents(page - 1);
      } else {
        fetchStudents(page);
      }
    } catch (err) {
      const msg = err.response?.data?.message || "خطا در حذف دانشجو";
      toast.error(msg);
    } finally {
      setModalIsOpen(false);
      setDeleteId("");
    }
  };

  const handleDownload = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/student/create_excel_file`, {
        responseType: "blob",
        headers: { Authorization: localStorage.getItem("token") },
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "Students.xlsx";
      link.click();
      URL.revokeObjectURL(url);
      toast.success("فایل اکسل دانلود شد");
    } catch {
      toast.error("خطا در دانلود فایل");
    }
  };

  const visiblePages = window.innerWidth < 768 ? 2 : 3;
  const half = Math.floor(visiblePages / 2);
  let startPage = Math.max(1, page - half);
  let endPage = Math.min(totalPages, startPage + visiblePages - 1);
  if (endPage - startPage + 1 < visiblePages) {
    startPage = Math.max(1, endPage - visiblePages + 1);
  }

  if (!localStorage.getItem("token")) return <Navigate to="/" />;

  const customStyles = {
    content: {
      borderRadius: "16px",
      margin: "auto",
      padding: "0",
      border: "none",
      width: "320px",
      height: "180px",
      background: "white",
    },
    overlay: {
      backgroundColor: "rgba(0,0,0,0.55)",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  };

  return (
    <div className="relative min-h-screen w-full bg-gray-800 flex items-center justify-center p-4">
      <AnimatedBackground />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl w-full max-w-3xl mx-auto border border-white/30 overflow-hidden mt-11"
      >
        {/* هدر */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <img
            src={backarrow}
            alt="back"
            className="w-8 h-8 cursor-pointer hover:scale-110 transition"
            onClick={() => navigate(-1)}
          />
          <h2 className="text-2xl font-bold text-gray-800">دانشجویان</h2>
          <div className="w-8" />
        </div>

        {/* جستجو و دانلود */}
        <div className="p-6 pt-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative w-full sm:max-w-[320px]">
              <input
                type="text"
                placeholder="جستجو بر اساس نام و نام خانوادگی..."
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-md border-2 border-gray-300 focus:border-gray-700 outline-none text-gray-800 font-iranSansNormal transition"
              />
              <IoSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
            </div>

            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-5 py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-md transition font-iranSansNormal"
            >
              <MdFileDownload size={20} />
              دانلود اکسل
            </button>
          </div>
        </div>

        {/* لیست دانشجویان */}
        <div className="px-6 pb-6">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-xl font-bold text-gray-600">
                در حال بارگذاری...
              </p>
            </div>
          ) : students.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xl font-bold text-gray-600">
                دانشجویی یافت نشد
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-800 text-white font-iranSansNormal">
                    <tr>
                      <th className="py-4 px-6 text-right">
                        نام و نام خانوادگی
                      </th>
                      <th className="py-4 px-6 text-center">شماره همراه</th>
                      <th className="pl-16 text-end">عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student, i) => (
                      <motion.tr
                        key={student._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="border-b border-gray-300 hover:bg-gray-100 transition"
                      >
                        <td className="py-4 px-6 text-base font-iranSansNormal text-gray-800">
                          {student.firstName} {student.lastName}
                        </td>
                        <td className="py-4 px-6 text-center text-gray-700">
                          {student.mobile}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex justify-end gap-6">
                            <CiEdit
                              size={26}
                              className="text-gray-700 cursor-pointer hover:text-gray-900 transition"
                              onClick={() =>
                                navigate("/Mainpage/EditStudent", {
                                  state: { studentid: student._id },
                                })
                              }
                            />

                            <img
                              src={courseListIcon}
                              alt="دوره‌ها"
                              className="w-7 h-7 cursor-pointer opacity-80 hover:opacity-100 transition"
                              onClick={() =>
                                navigate("/Mainpage/StudentCourses", {
                                  state: { studentid: student._id },
                                })
                              }
                            />

                            <img
                              src={trashIcon}
                              alt="حذف"
                              className="w-7 h-7 cursor-pointer opacity-80 hover:opacity-100 transition"
                              onClick={() => {
                                setDeleteId(student._id);
                                setModalIsOpen(true);
                              }}
                            />
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* صفحه‌بندی */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-3 mt-8">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-full disabled:opacity-50 hover:bg-gray-200 transition"
                  >
                    <FaArrowRight size={20} />
                  </button>

                  {Array.from(
                    { length: endPage - startPage + 1 },
                    (_, i) => startPage + i
                  ).map((num) => (
                    <motion.button
                      key={num}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setPage(num)}
                      className={`w-8 h-8 text-sm font-medium rounded-md transition ${
                        num === page
                          ? "bg-gray-800 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {num}
                    </motion.button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-full disabled:opacity-50 hover:bg-gray-200 transition"
                  >
                    <FaArrowLeft size={20} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>

      {/* مودال حذف */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        style={customStyles}
      >
        <div className="flex flex-col items-center justify-center h-full px-6 text-center bg-gray-200 font-iranSansNormal">
          <p className="text-gray-800 text-lg font-semibold">
            آیا از حذف این دانشجو اطمینان دارید؟
          </p>
          <div className="flex gap-4 mt-6">
            <button
              onClick={handleDelete}
              className="px-8 py-2.5 rounded-lg bg-red-600/90 text-white text-sm font-medium"
            >
              بله
            </button>
            <button
              onClick={() => setModalIsOpen(false)}
              className="px-8 py-2.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium"
            >
              خیر
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AllStudents;
